import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { automationApi, ApiError } from '@/lib/api';
import { showApiError } from '@/lib/errors';
import { getLimitErrorMessage, isLimitExceededError } from '@/hooks/useFeatureLimits';

export interface AutomationFlow {
  id: string;
  name: string;
  description?: string;
  triggerType?: string;
  triggerConfig?: Record<string, any>;
  flowData?: FlowDefinition;
  businessAccountId: string;
  userId: string;
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED';
  createdAt: string;
  updatedAt: string;
  businessAccount?: {
    id: string;
    name: string;
    phoneNumber?: string;
  };
  user?: {
    id: string;
    name: string;
    email: string;
  };
  // Execution stats included in the response
  stats?: {
    totalRuns: number;
    successRate: number;
    lastRun: string | null;
  };
  // Flat stats properties (API compatibility)
  totalRuns?: number;
  successRate?: number | string;
  lastRun?: string | null;
}

export interface TriggerConfig {
  type: 'message_received' | 'keyword' | 'tag_added' | 'time_based' | 'webhook';
  conditions: Record<string, any>;
}

// For API compatibility
export type TriggerConfigData = Record<string, any>;

export interface FlowDefinition {
  nodes: FlowNode[];
  edges: FlowEdge[];
}

export interface FlowNode {
  id: string;
  type: string;
  data: Record<string, any>;
  position: { x: number; y: number };
}

export interface FlowEdge {
  id: string;
  source: string;
  target: string;
  type?: string;
}

export function useAutomationFlows(params?: {
  businessAccountId?: string;
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['automation-flows', params?.businessAccountId, params?.status, params?.search, params?.page, params?.limit],
    queryFn: async () => {
      const { businessAccountId, status, search, page = 1, limit = 10 } = params || {};
      const offset = (page - 1) * limit;

      const result = await automationApi.getAll(businessAccountId, status, search, limit, offset);

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      // Return both data and count for pagination
      return {
        data: result.data || [],
        count: result.count || 0
      };
    },
    enabled: !!params?.businessAccountId,
    placeholderData: keepPreviousData, // Keep showing old results while new search loads
    staleTime: 30 * 1000, // 30s — search results should refresh reasonably fast
    gcTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnMount: true, // Refetch on mount for fresh data
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });
}

export function useCreateAutomationFlow() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (flowData: {
      businessAccountId: string;
      name: string;
      description?: string;
      triggerType: string;
      triggerConfig: TriggerConfigData;
      flowData: FlowDefinition;
      settings?: Record<string, any>;
      userId: string;
    }) => {
      const result = await automationApi.create({
        businessAccountId: flowData.businessAccountId,
        name: flowData.name,
        description: flowData.description,
        triggerType: flowData.triggerType,
        triggerConfig: flowData.triggerConfig,
        flowData: flowData.flowData,
        userId: flowData.userId,
      });

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation-flows'] });
      toast({ variant: "success",
        title: "Automation flow created",
        description: "Your automation flow has been successfully created.",
      });
    },
    onError: (error: any) => {
      const errorMessage = getLimitErrorMessage(error);
      toast({
        title: isLimitExceededError(error) ? "Limit Reached" : "Error creating automation flow",
        description: errorMessage,
        variant: "destructive",
        duration: isLimitExceededError(error) ? 8000 : 5000,
      });
    }
  });
}

export function useUpdateAutomationFlow() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<AutomationFlow> }) => {
      const result = await automationApi.update(id, updates);

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation-flows'] });
      toast({ variant: "success",
        title: "Automation flow updated",
        description: "Your automation flow has been updated successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update automation flow. Please try again.",
        variant: "destructive",
      });
    },
  });
}

export function useExecuteAutomationFlow() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ flowId, contactId, triggerData, dryRun = true }: { flowId: string; contactId: string; triggerData?: any; dryRun?: boolean }) => {
      const result = await automationApi.execute(flowId, { contactId, triggerData, dryRun });

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation-flows'] });
      toast({ variant: "success",
        title: "Automation flow executed",
        description: "Your automation flow has been executed successfully.",
      });
    },
    onError: (error) => {
      showApiError(error, { fallback: { title: "Error executing automation flow" } });
    }
  });
}

export function useAutomationFlow(flowId?: string) {
  return useQuery({
    queryKey: ['automation-flow', flowId],
    queryFn: async () => {
      if (!flowId) return null;

      const result = await automationApi.getById(flowId);

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return result.data;
    },
    enabled: !!flowId,
    staleTime: 15000, // Data stays fresh for 15 seconds
    gcTime: 2 * 60 * 1000, // Cache for 2 minutes
  });
}

export function useFlowExecutions(flowId?: string) {
  return useQuery({
    queryKey: ['flow-executions', flowId],
    queryFn: async () => {
      if (!flowId) return [];

      const result = await automationApi.getExecutions(flowId);

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return result.data;
    },
    enabled: !!flowId,
    staleTime: 10000, // Data stays fresh for 10 seconds
    gcTime: 2 * 60 * 1000, // Cache for 2 minutes
  });
}

export function useFlowExecutionSummaries(flowIds: string[]) {
  return useQuery({
    queryKey: ['flow-execution-summaries', flowIds],
    queryFn: async () => {
      if (flowIds.length === 0) return [];

      const result = await automationApi.getExecutionSummaries(flowIds);

      return result;
    },
    enabled: flowIds.length > 0,
    staleTime: 30000, // Data stays fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnMount: false, // Don't refetch if we have cached data
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });
}

export function useActivateFlow() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (flowId: string) => {
      const result = await automationApi.publish(flowId);

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation-flows'] });
      toast({ variant: "success",
        title: "Flow activated",
        description: "Your automation flow has been activated successfully.",
      });
    },
    onError: (error) => {
      showApiError(error, { fallback: { title: "Error activating flow" } });
    }
  });
}

export function usePauseFlow() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (flowId: string) => {
      const result = await automationApi.pause(flowId);

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation-flows'] });
      toast({ variant: "success",
        title: "Flow paused",
        description: "Your automation flow has been paused successfully.",
      });
    },
    onError: (error) => {
      showApiError(error, { fallback: { title: "Error pausing flow" } });
    }
  });
}

export function useResumeFlow() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (flowId: string) => {
      const result = await automationApi.resume(flowId);

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation-flows'] });
      toast({ variant: "success",
        title: "Flow resumed",
        description: "Your automation flow has been resumed successfully.",
      });
    },
    onError: (error) => {
      showApiError(error, { fallback: { title: "Error resuming flow" } });
    }
  });
}


export function useDuplicateFlow() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      flowId,
      name,
      businessAccountId,
      userId
    }: {
      flowId: string;
      name: string;
      businessAccountId: string;
      userId: string;
    }) => {
      const result = await automationApi.duplicate(flowId, {
        name,
        businessAccountId,
        userId
      });

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation-flows'] });
      toast({ variant: "success",
        title: "Flow duplicated",
        description: "Your automation flow has been duplicated successfully.",
      });
    },
    onError: (error) => {
      showApiError(error, { fallback: { title: "Error duplicating flow" } });
    }
  });
}

export function useDeleteAutomationFlow() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (flowId: string) => {
      const result = await automationApi.delete(flowId);

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation-flows'] });
      toast({ variant: "success",
        title: "Flow deleted",
        description: "Automation flow deleted successfully.",
      });
    },
    onError: (error) => {
      showApiError(error, { fallback: { title: "Couldn't delete flow" } });
    }
  });
}
